import type { Wiring as BaseWiring } from '#~/common/editor/index.js';
import type { Minimist } from '#~/device/index.js';
import type { Device } from '@studio/models/device';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { createContext } from 'react';

/**
 * What a caller resolves at commit time and hands to `handleCommit` instead of writing
 * it to the store. A store write there would reach the preview Mediator and Discovery
 * while the commit runs.
 */
export type CommitOptions = Readonly<{
  /** Commit this control in place of the candidate's. */
  control?: T_Maybe<Minimist['control']>;
}>;

/**
 * Extended editor wiring for the add-video-source workflow.
 *
 * In Multi-Add Phase 1, this adds `handleBulkCommit`, the bulk-add
 * commit pipeline that runs `addDevice()` in parallel via
 * `Promise.allSettled` for the multi-selected discoveries (selection
 * collection + per-device candidate construction live elsewhere).
 *
 * Per `stay-in-scope` (ai.knowledge/global): the extension is local to
 * the add/ module — common/editor's `Wiring` type stays shared and
 * untouched, so the single-add editor and other consumers are
 * unaffected by W7's bulk-mode addition.
 *
 * Per `think-in-systems` (ai.knowledge/global): the bulk handler returns
 * a small metadata object (`{ successCount, newDeviceIds }`) so the
 * caller (footer's `handleBulkClick`) can drive UI side-effects
 * (post-success toast suppression, tile-focus return) without
 * re-deriving the outcome from the store.
 */
export type Wiring = BaseWiring &
  Readonly<{
    /** Single-add commit; resolves to the added device's id, or undefined on failure. */
    handleCommit: (options?: CommitOptions) => Promise<T_Maybe<Device['id']>>;

    /**
     * Bulk commit handler. Resolves AFTER the modal close + focus return
     * have been dispatched (the orchestrator owns timing so the caller
     * doesn't need its own dwell — see W5 footer for the visible
     * "Adding…" CTA dwell, which is independent).
     *
     *   - Promise.allSettled (NOT Promise.all) so a single failure
     *     never short-circuits the batch.
     *   - Zero-success → `post()` toast ("0 of N sources added").
     *   - Partial-success / full-success → no toast (chips on tiles
     *     handle per-device visibility).
     *   - Modal close timing: MAX(allSettled, 300ms) — the 300ms floor
     *     mirrors the footer CTA dwell so the user always sees the morph.
     *   - Focus return: first new tile when successCount > 0; otherwise
     *     the dialog's own trigger-element return (Radix default).
     */
    handleBulkCommit?: T_Maybe<() => Promise<BulkCommitResult>>;
  }>;

/** Bulk commit outcome metadata. */
export type BulkCommitResult = Readonly<{
  /** Count of devices that successfully reached `Commands.add` resolution. */
  successCount: number;

  /** Count of selected devices that the pipeline attempted to commit. */
  attemptedCount: number;

  /**
   * Count of devices the user explicitly skipped via the ONVIF
   * credentials prompt (or the second probe failed after entering
   * credentials). Reported by the toast so the user understands
   * `X of N added; Y skipped` even when the rest succeeded.
   * Per Multi-Add Phase 1 manual QA (ONVIF sequential probe flow).
   */
  skippedCount: number;

  /** IDs of newly-committed devices, in selection order. */
  newDeviceIds: Device['id'][];

  /**
   * Count of Iris-enabled discoveries that completed their cloud
   * `linkAgent` mutation successfully. Caller-observable so the React-
   * side wiring can fire a renderer reload AFTER the modal closes,
   * matching the contract in `credentials-dialog.tsx::handleConfirm`
   * (single-add reloads via `ElectronAPI.onReloadStudio()` +
   * `window.location.reload()` after a successful link so the just-
   * joined agent's state-synced devices render in the sidebar without
   * the user manually refreshing). Zero in batches that contain only
   * ONVIF / RTSP / NDI / browsermedia. Per Multi-Add Phase 1 manual
   * QA — Iris-enabled UX consistency with single-add.
   */
  irisLinkedCount: number;
}>;

/** Wiring context. */
export const Context = createContext<T_Maybe<Wiring>>(undefined);
